<script setup>
import { ref, watch, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { Users } from 'lucide-vue-next'
import { useAuth } from '../stores/useAuth.js'
import { supabase } from '../supabase.js'
import { useToast } from '../composables/useToast.js'

const route = useRoute()
const auth = useAuth()
const { showToast } = useToast()

const showConfirmation = ref(false)
const massToken = ref(null)
const shopPreview = ref(null) // { id, name, location }
const isResolving = ref(false)
const isSubmitting = ref(false)

const findToken = () => {
    const urlParams = new URLSearchParams(window.location.search)
    let token = urlParams.get('mass_token')

    if (token) {
        sessionStorage.setItem('mass_token', token)
        return token
    }

    token = sessionStorage.getItem('mass_token')
    if (token) return token

    token = localStorage.getItem('mass_token')
    if (token) {
        sessionStorage.setItem('mass_token', token)
        localStorage.removeItem('mass_token')
        return token
    }

    return null
}

const resolveShop = async (token) => {
    isResolving.value = true
    try {
        const { data, error } = await supabase.rpc('get_shop_by_mass_token', { token_input: token })
        if (error) throw error
        return data || null
    } catch (e) {
        console.error('[GlobalMassInvitationHandler] resolveShop failed:', e)
        return null
    } finally {
        isResolving.value = false
    }
}

const checkInvite = async () => {
    if (showConfirmation.value || isResolving.value) return

    const token = findToken()
    const user = auth.authState.user
    const profile = auth.authState.profile

    // Only handle for fully signed-up users; new signups go through SignUpFlow step 26.
    if (!token || !user || !profile?.signupCompleted) return

    const shop = await resolveShop(token)
    if (!shop) {
        // Stale or invalid token — drop it so we don't loop.
        clearToken()
        return
    }

    // Persist the invite server-side immediately so it survives tab close,
    // navigation, etc. The notification feed is the durable record; the modal
    // below is just immediate UX. RPC is idempotent and de-dupes per shop.
    try {
        await supabase.rpc('claim_mass_invite', { token_input: token })
    } catch (e) {
        console.warn('[GlobalMassInvitationHandler] claim_mass_invite failed:', e?.message)
    }

    massToken.value = token
    shopPreview.value = shop
    showConfirmation.value = true
}

watch(
    () => [auth.authState.user, auth.authState.profile, route.query],
    () => { checkInvite() },
    { deep: true }
)

onMounted(() => {
    checkInvite()
})

const clearToken = () => {
    massToken.value = null
    shopPreview.value = null
    sessionStorage.removeItem('mass_token')
    localStorage.removeItem('mass_token')
    const url = new URL(window.location)
    if (url.searchParams.has('mass_token')) {
        url.searchParams.delete('mass_token')
        window.history.replaceState({}, '', url)
    }
}

const handleAccept = async () => {
    if (!shopPreview.value) return
    isSubmitting.value = true
    try {
        const { data, error } = await supabase.rpc('request_to_join_shop', {
            shop_id_input: shopPreview.value.id
        })
        if (error) throw error

        if (data?.status === 'already_member') {
            showToast("You are already a member of this shop.", "info")
        } else if (data?.status === 'already_pending') {
            showToast("You already requested to join this shop.", "info")
        } else {
            showToast("Request sent. The team will be notified.", "success")
        }

        clearToken()
        showConfirmation.value = false
    } catch (e) {
        console.error('[GlobalMassInvitationHandler] accept failed:', e)
        showToast(e.message || "Failed to send join request.", "error")
    } finally {
        isSubmitting.value = false
    }
}

const handleDismiss = () => {
    // The notification was already persisted on detect (claim_mass_invite),
    // so dismissing is just closing the modal — the invite stays in their feed.
    clearToken()
    showConfirmation.value = false
}
</script>

<template>
    <div v-if="showConfirmation"
        class="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
        <div
            class="bg-white dark:bg-slate-900 rounded-2xl shadow-xl max-w-md w-full p-8 text-center space-y-6 animate-in zoom-in-95 duration-200 border border-gray-100 dark:border-gray-800 relative">

            <button @click="handleDismiss"
                class="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                </svg>
            </button>

            <div
                class="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users :size="32" />
            </div>

            <h2 class="text-2xl font-bold text-gray-900 dark:text-white">
                {{ shopPreview?.name || 'A team' }} is inviting you to join their team
            </h2>

            <p v-if="shopPreview?.location" class="text-sm text-gray-500 dark:text-slate-400">
                {{ shopPreview.location }}
            </p>

            <div class="bg-gray-50 dark:bg-slate-800 p-4 rounded-xl border border-gray-100 dark:border-slate-700">
                <p class="text-sm text-gray-500 dark:text-gray-400 mb-1">You are currently logged in as</p>
                <p class="font-bold text-gray-900 dark:text-gray-200 break-all">{{ auth.authState.user?.email }}</p>
            </div>

            <p class="text-gray-600 dark:text-gray-400 text-sm leading-relaxed">
                Accepting will send a join request. A team admin will need to approve before you join.
            </p>

            <div class="space-y-3 pt-2">
                <button @click="handleAccept" :disabled="isSubmitting"
                    class="w-full py-3.5 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors shadow-lg shadow-blue-600/20 disabled:opacity-60 disabled:cursor-not-allowed">
                    {{ isSubmitting ? 'Sending…' : 'Request to Join' }}
                </button>
                <button @click="handleDismiss" :disabled="isSubmitting"
                    class="w-full py-3 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 font-medium text-sm">
                    Not now
                </button>
            </div>
        </div>
    </div>
</template>
